<script setup lang="ts">
import BlogSidebar from '~/components/blogs/blogSidebar.vue'

const { data: allBlogs } = useSanityQuery(getAllBlogs)
const router = useRouter()
const route = useRoute()

// Reactive state for slideover
const isSlideoverOpen = computed(() => !!route.query.blog)
const selectedBlog = computed(() => {
  if (!isSlideoverOpen.value || !allBlogs.value) return null
  const blogSlug = route.query.blog as string
  return (allBlogs.value as Blog[]).find((blog) => blog.slug === blogSlug)
})

// Function to open blog modal with query parameter
const openBlogSlideover = (blog: Blog) => {
  router.push({
    path: route.path,
    query: { ...route.query, blog: blog.slug },
  })
}

// Function to close slideover
const closeSlideover = () => {
  const { blog, ...restQuery } = route.query
  router.push({
    path: route.path,
    query: restQuery,
  })
}

// Group consecutive list items together
const groupedBlocks = computed(() => {
  if (!selectedBlog.value?.text) return []

  const result: any[] = []
  let i = 0

  while (i < selectedBlog.value.text.length) {
    const block = selectedBlog.value.text[i] as any

    // Check if this is a list item
    if (block._type === 'block' && block.listItem) {
      const listType = block.listItem
      const listItems = [block]

      // Collect consecutive items of the same list type
      let j = i + 1
      while (j < selectedBlog.value.text.length) {
        const nextBlock = selectedBlog.value.text[j] as any
        if (nextBlock._type === 'block' && nextBlock.listItem === listType) {
          listItems.push(nextBlock)
          j++
        } else {
          break
        }
      }

      result.push({
        type: 'list',
        listType,
        items: listItems,
        _key: `list-${i}`,
      })

      i = j
    } else {
      result.push({
        type: 'block',
        block,
        _key: block._key || `block-${i}`,
      })
      i++
    }
  }

  return result
})
</script>

<template>
  <section class="px-bleed bg-light-grey min-h-screen py-20 transition-all duration-500">
    <div v-if="allBlogs && (allBlogs as any[]).length > 0" class="mx-auto max-w-7xl">
      <!-- Main Layout: 2x2 Grid + Featured Card -->
      <div class="grid gap-8 lg:grid-cols-5">
        <!-- Left Side: 2x2 Grid of Text-Only Blog Cards -->
        <div class="space-y-8 lg:col-span-3">
          <!-- First Row -->
          <div class="grid gap-8 md:grid-cols-2">
            <div
              v-for="(blog, index) in (allBlogs as any[]).slice(1, 3)"
              :key="index + 1"
              class="group cursor-pointer"
              :class="{
                'border-b border-gray-300 pr-6 pb-8 md:border-r md:border-b-0 md:pb-0': index === 0,
              }"
              @click="openBlogSlideover(blog)"
            >
              <div class="mb-3 flex items-center justify-between">
                <span
                  v-if="blog.category"
                  class="bg-neon-yellow px-2 py-1 font-mono text-xs font-normal uppercase"
                  >{{ blog.category }}</span
                >
                <span class="text-dark-grey text-sm">
                  {{
                    new Date(blog.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })
                  }}
                </span>
              </div>
              <h2 class="font-display mb-3 text-xl uppercase font-stretch-expanded">
                {{ blog.title.length > 70 ? blog.title.substring(0, 70) + '...' : blog.title }}
              </h2>
              <p class="mb-4 text-base leading-relaxed">
                {{
                  blog.description.length > 250
                    ? blog.description.substring(0, 250) + '...'
                    : blog.description
                }}
              </p>
              <!-- just a visual representation of a link -->
              <p
                v-if="true"
                class="group flex items-center gap-2 font-mono text-sm whitespace-nowrap uppercase"
              >
                <span
                  class="logomark h-[.6lh] w-3 -skew-x-[20deg] bg-current transition-all duration-200 group-hover:w-7"
                />
                <span class="transition-transform duration-500 group-hover:translate-x-2">
                  Read Article
                </span>
              </p>
            </div>
          </div>

          <!-- Divider -->
          <hr class="border-gray-300" />

          <!-- Second Row -->
          <div class="grid gap-8 md:grid-cols-2">
            <div
              v-for="(blog, index) in (allBlogs as any[]).slice(3, 5)"
              :key="index + 3"
              class="group cursor-pointer"
              :class="{
                'border-b border-gray-300 pr-6 pb-8 md:border-r md:border-b-0 md:pb-0': index === 0,
              }"
              @click="openBlogSlideover(blog)"
            >
              <div class="mb-3 flex items-center justify-between">
                <span class="bg-neon-yellow dark-grey px-2 py-1 font-mono text-xs font-normal"
                  >Fashion</span
                >
                <span class="text-dark-grey text-sm">
                  {{
                    new Date(blog.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })
                  }}
                </span>
              </div>
              <h2 class="font-display mb-3 text-xl uppercase font-stretch-expanded">
                {{ blog.title.length > 70 ? blog.title.substring(0, 70) + '...' : blog.title }}
              </h2>
              <p class="mb-4 text-base leading-relaxed">
                {{
                  blog.description.length > 250
                    ? blog.description.substring(0, 250) + '...'
                    : blog.description
                }}
              </p>
              <!-- just a visual representation of a link -->
              <p
                v-if="true"
                class="group flex items-center gap-2 font-mono text-sm whitespace-nowrap uppercase"
              >
                <span
                  class="logomark h-[.6lh] w-3 -skew-x-[20deg] bg-current transition-all duration-200 group-hover:w-7"
                />
                <span class="transition-transform duration-500 group-hover:translate-x-2">
                  Read Article
                </span>
              </p>
            </div>
          </div>
        </div>

        <!-- Right Side: Featured Blog Card with Large Image (latest blog) -->
        <div class="lg:col-span-2">
          <div
            v-if="(allBlogs as any[])[0]"
            class="group relative cursor-pointer overflow-hidden"
            @click="openBlogSlideover((allBlogs as any[])[0])"
          >
            <!-- Large Featured Image -->
            <CommonMedia
              v-if="(allBlogs as any[])[0].media"
              :sizes="['100vw']"
              :aspect="4 / 5"
              :fill="'both'"
              :media="(allBlogs as any[])[0].media"
            />

            <!-- Overlay -->
            <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

            <!-- Text Content -->
            <div class="absolute bottom-8 left-8 max-w-sm text-white">
              <h2 class="font-display mb-3 text-3xl uppercase font-stretch-expanded">
                {{
                  (allBlogs as any[])[0].title.length > 40
                    ? (allBlogs as any[])[0].title.substring(0, 40) + '...'
                    : (allBlogs as any[])[0].title
                }}
              </h2>
              <p class="mb-4 text-sm leading-relaxed">
                {{
                  (allBlogs as any[])[0].description.length > 150
                    ? (allBlogs as any[])[0].description.substring(0, 150) + '...'
                    : (allBlogs as any[])[0].description
                }}
              </p>
              <!-- just a visual representation of a link -->
              <p
                v-if="true"
                class="group flex items-center gap-2 font-mono text-sm whitespace-nowrap uppercase"
              >
                <span
                  class="logomark h-[.6lh] w-3 -skew-x-[20deg] bg-current transition-all duration-200 group-hover:w-7"
                />
                <span class="text-sm transition-transform duration-500 group-hover:translate-x-2">
                  Read Article
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- More Articles - Full Width Horizontal Collage Design -->
    <div v-if="allBlogs && (allBlogs as any[]).length > 5" class="mt-16 w-full">
      <div
        class="relative my-5 mt-16 mb-16 flex justify-between pt-4 font-mono text-sm uppercase select-none"
      >
        <div
          ref="border"
          class="line-border absolute top-0 left-0 h-[1px] w-full origin-center bg-black/50"
        />
        <h2 ref="title">more articles</h2>
        <p ref="indicator">[02]</p>
      </div>

      <!-- 5-Column Grid Layout - Full Width -->
      <div class="w-full">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
          <div
            v-for="(blog, idx) in (allBlogs as any[]).slice(5, 11)"
            :key="idx + 5"
            class="group cursor-pointer overflow-hidden bg-white transition-all duration-500 ease-in-out hover:shadow-xl hover:shadow-black/20"
            @click="openBlogSlideover(blog)"
          >
            <!-- Vertical Panel Structure -->
            <div class="flex h-[660px] flex-col">
              <!-- Top Image Section -->
              <div class="relative flex-1 overflow-hidden">
                <CommonMedia
                  v-if="blog.media"
                  :sizes="['100vw']"
                  :aspect="3 / 5"
                  :fill="'both'"
                  :media="blog.media"
                />

                <!-- Hover Title Overlay -->
                <div
                  class="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 transition-opacity duration-500 ease-in-out group-hover:opacity-100"
                >
                  <div class="overly-title vertical-text text-center">
                    <div class="text-lg font-bold text-white">
                      {{
                        blog.title.length > 40 ? blog.title.substring(0, 40) + '...' : blog.title
                      }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Bottom Color Block Section -->
              <div
                class="flex h-40 flex-col items-center justify-center px-4 text-white"
                :class="{
                  'bg-black': idx % 2 === 0,
                  'bg-neon-yellow text-black': idx % 2 === 1,
                }"
              >
                <!-- Location/Description -->
                <p
                  class="text-start font-mono text-sm font-normal"
                  :class="{
                    'text-black': idx % 2 === 1,
                    'text-white': idx % 2 === 0,
                  }"
                >
                  {{
                    blog.description.length > 150
                      ? blog.description.substring(0, 150) + '...'
                      : blog.description
                  }}
                </p>
                <div class="w-full sm:block md:hidden">
                  <p
                    v-if="true"
                    class="group flex items-center gap-2 pt-3 font-mono text-sm whitespace-nowrap uppercase"
                  >
                    <span
                      class="logomark h-[.6lh] w-3 -skew-x-[20deg] bg-current transition-all duration-200 group-hover:w-7"
                      :class="{ 'text-black': idx % 2 === 1, 'text-white': idx % 2 === 0 }"
                    />
                    <span
                      class="text-sm transition-transform duration-500 group-hover:translate-x-2"
                      :class="{ 'text-black': idx % 2 === 1, 'text-white': idx % 2 === 0 }"
                    >
                      Read Article
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Blog Sidebar Component -->
  <BlogSidebar
    :is-slideover-open="isSlideoverOpen"
    :selected-blog="selectedBlog"
    :grouped-blocks="groupedBlocks"
    @close="closeSlideover"
  />
</template>
